/**
 * Session Management Utilities
 * Handles secure token storage and session persistence
 */

import type { SessionData, UserProfile } from '../types';

// Storage keys for session data
const STORAGE_KEYS = {
  SESSION_TOKEN: 'itn_auth_token',
  SESSION_DATA: 'itn_session_data',
  REDIRECT_URL: 'itn_redirect_url'
} as const;

export class SessionManager {
  /**
   * Store authentication token securely
   */
  setToken(token: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
    } catch (error) {
      console.warn('Failed to store authentication token:', error);
    }
  }

  /**
   * Retrieve stored authentication token
   */
  getToken(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
    } catch (error) {
      console.warn('Failed to retrieve authentication token:', error);
      return null;
    }
  }

  /**
   * Store complete session data
   */
  setSessionData(sessionData: SessionData): void {
    try {
      const serializedData = JSON.stringify(sessionData);
      localStorage.setItem(STORAGE_KEYS.SESSION_DATA, serializedData);
      this.setToken(sessionData.token);
    } catch (error) {
      console.warn('Failed to store session data:', error);
    }
  }

  /**
   * Retrieve complete session data
   */
  getSessionData(): SessionData | null {
    try {
      const serializedData = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
      if (!serializedData) return null;

      const sessionData: SessionData = JSON.parse(serializedData);
      
      // Check if session has expired
      if (sessionData.expiresAt && Date.now() > sessionData.expiresAt) {
        this.clearSession();
        return null;
      }

      return sessionData;
    } catch (error) {
      console.warn('Failed to retrieve session data:', error);
      this.clearSession(); // Clear corrupted data
      return null;
    }
  }

  /**
   * Create session data from authentication response
   */
  createSessionData(token: string, user: UserProfile, redirectUrl?: string): SessionData {
    const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours from now
    
    return {
      token,
      user,
      expiresAt,
      redirectUrl
    };
  }

  /**
   * Check if user has valid session
   */
  hasValidSession(): boolean {
    const sessionData = this.getSessionData();
    return sessionData !== null && sessionData.expiresAt > Date.now();
  }

  /**
   * Get current user from session
   */
  getCurrentUser(): UserProfile | null {
    const sessionData = this.getSessionData();
    return sessionData?.user || null;
  }

  /**
   * Store redirect URL for post-authentication navigation
   */
  setRedirectUrl(url: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.REDIRECT_URL, url);
    } catch (error) {
      console.warn('Failed to store redirect URL:', error);
    }
  }

  /**
   * Retrieve and clear redirect URL
   */
  getAndClearRedirectUrl(): string | null {
    try {
      const url = localStorage.getItem(STORAGE_KEYS.REDIRECT_URL);
      if (url) {
        localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL);
      }
      return url;
    } catch (error) {
      console.warn('Failed to retrieve redirect URL:', error);
      return null;
    }
  }

  /**
   * Update session expiry time (for session extension)
   */
  extendSession(additionalHours: number = 24): boolean {
    try {
      const sessionData = this.getSessionData();
      if (!sessionData) return false;

      sessionData.expiresAt = Date.now() + (additionalHours * 60 * 60 * 1000);
      this.setSessionData(sessionData);
      return true;
    } catch (error) {
      console.warn('Failed to extend session:', error);
      return false;
    }
  }

  /**
   * Clear all session data and tokens
   */
  clearSession(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.SESSION_DATA);
      localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL);
    } catch (error) {
      console.warn('Failed to clear session data:', error);
    }
  }

  /**
   * Check if session will expire soon (within specified minutes)
   */
  isSessionExpiringSoon(withinMinutes: number = 30): boolean {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const warningTime = Date.now() + (withinMinutes * 60 * 1000);
    return sessionData.expiresAt <= warningTime;
  }

  /**
   * Get session time remaining in milliseconds
   */
  getSessionTimeRemaining(): number {
    const sessionData = this.getSessionData();
    if (!sessionData) return 0;

    return Math.max(0, sessionData.expiresAt - Date.now());
  }

  /**
   * Initialize session manager and clean up expired sessions
   */
  initialize(): void {
    // Clean up any expired sessions on initialization
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.expiresAt <= Date.now()) {
      this.clearSession();
    }
  }
}

// Export singleton instance for consistent usage across the application
export const sessionManager = new SessionManager();